import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BadgeComponent, ButtonDirective, CardBodyComponent, CardComponent, SpinnerComponent } from '@coreui/angular-pro';
import { OrderService, Web3ConfirmationComponent } from 'credo';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { interval } from 'rxjs';

import { CountryOption, countryName, countryOptions } from '../../../services/countries';
import {
  MarketingPoolService, MpAcceptResponse, MpDocument, MpInvestment, MpOverview, MpPaymentMethod, MpReferral, MpStatus,
  MpToken, mpErrorCode, mpErrorKey, mpErrorParams, saveBlob
} from '../../../services/marketing-pool.service';

type Tab = 'investments' | 'info' | 'referrals';

const STATUS_COLOR: Record<MpStatus, string> = {
  offered: 'primary', pending: 'secondary', locked: 'warning', active: 'success', due: 'info', closed: 'dark',
  withdrawn: 'secondary', reversed: 'danger'
};
const CONTRACT_KEY = 'marketing_pool_contract';
const DAY_MS = 86_400_000;
const AMOUNT = /^\d+(\.\d{1,2})?$/;
const DECIMAL = /^\d+(\.\d+)?$/;

/** A 2-decimal amount as integer cents, or null. String maths only — no float on money. */
function toCents(raw: string): number | null {
  const v = raw.trim().replace(',', '.');
  if (!AMOUNT.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2));
}

/** A decimal string as { int, scale } (e.g. "6.25" → 625 / 10^2), or null. */
function toScaled(raw: string | null | undefined): { int: number; scale: number } | null {
  const v = (raw ?? '').trim();
  if (!DECIMAL.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  return { int: Number(whole + frac), scale: frac.length };
}

/**
 * Marketing Pool – Founding Partner — the member page.
 *
 * Self-service since the customer correction of 2026-10-28: an unlocked
 * partner enters their own participation amount (min 500 USD, any number of
 * further participations), sees the total payout = amount × the factor
 * management published, and POST /investments turns that into their own draft
 * (status `offered`). The page goes straight into the acceptance view, so the
 * personalised contract with Investment-ID, amount, factor and total payout is
 * on screen BEFORE anything is ticked; the partner accepts the ONE contract
 * (Founding Partner Vereinbarung), pays, and follows the participation until it
 * is paid out in full. No office offer, no membership precondition.
 *
 * A partner sees ONLY their own contract data — no commissions. The backend
 * decides everything (its total is authoritative); the form only mirrors its
 * rules so the partner is not sent into an avoidable error.
 */
@Component({
  selector: 'app-marketing-pool-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslatePipe, CardComponent, CardBodyComponent, ButtonDirective,
            BadgeComponent, SpinnerComponent, Web3ConfirmationComponent],
  templateUrl: './marketing-pool-page.component.html',
  styleUrls: ['./marketing-pool-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarketingPoolPageComponent {
  private service    = inject(MarketingPoolService);
  private orders     = inject(OrderService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private destroyRef = inject(DestroyRef);

  loading  = signal(true);
  blocked  = signal<'not_available' | 'error' | null>(null);
  overview = signal<MpOverview | null>(null);
  tab      = signal<Tab>('investments');
  /** True while the contract is still a draft (banner on the info tab). */
  docDraft = signal(false);

  /** server_time − client clock at load: the countdown never trusts the client clock alone. */
  private serverOffset = signal(0);
  private tick = signal(Date.now());

  investments = computed(() => this.overview()?.investments ?? []);
  offers      = computed(() => this.investments().filter(i => i.status === 'offered'));
  contracts   = computed(() => this.investments().filter(i => i.status !== 'offered'));
  referrals   = computed<MpReferral[]>(() => this.overview()?.referrals ?? []);
  showReferrals = computed(() => !!this.overview()?.referrer || this.referrals().length > 0);

  /** Why a draft cannot be accepted right now, or null. */
  acceptBlock = computed<'not_open' | null>(() => {
    const o = this.overview();
    if (!o) return null;
    return o.can_accept ? null : 'not_open';
  });

  // ----- New participation (self-service, customer correction 2026-10-28) ------------------
  /** Why no new participation can be entered right now, or null. */
  investBlock = computed<'not_open' | 'factor_missing' | null>(() => {
    const o = this.overview();
    if (!o || o.can_invest) return null;
    if (!o.can_accept || !o.open_for_investments) return 'not_open';
    if (!o.payout_factor) return 'factor_missing';
    return 'not_open';
  });

  newAmount = '';
  newTick = signal(0);
  onNewInput(): void { this.newTick.update(n => n + 1); }
  newCents = computed(() => { this.newTick(); return toCents(this.newAmount); });
  minimumCents = computed(() => toCents(this.overview()?.minimum_amount ?? '500') ?? 50_000);
  newAmountError = computed(() => {
    this.newTick();
    if (!this.newAmount.trim()) return null;
    const cents = this.newCents();
    if (cents === null) return 'marketing_pool.errors.amount_invalid';
    if (cents < this.minimumCents()) return 'marketing_pool.errors.amount_below_minimum';
    return null;
  });

  /**
   * Preview only: amount × factor, half-up to cents, in integers. The backend
   * computes the binding figure and the acceptance view shows that one.
   */
  previewTotal = computed<string | null>(() => {
    const cents = this.newCents();
    const factor = toScaled(this.overview()?.payout_factor);
    if (cents === null || !factor || this.newAmountError()) return null;
    const unit = 10 ** factor.scale;
    const total = Math.floor((cents * factor.int * 2 + unit) / (2 * unit));
    return (total / 100).toFixed(2);
  });

  canCreate = computed(() => {
    this.newTick();
    return !this.creating() && !this.investBlock() && !!this.overview()?.can_invest
      && this.newCents() !== null && !this.newAmountError();
  });
  creating    = signal(false);
  createError = signal<string | null>(null);
  /** Set when an acceptance failed with `factor_changed`: the partner enters the amount again. */
  factorNotice = signal(false);

  // ----- Acceptance view -----------------------------------------------------------------
  accepting   = signal<MpInvestment | null>(null);
  documents   = signal<MpDocument[]>([]);
  docsLoading = signal(false);
  docsError   = signal<string | null>(null);
  contractDoc = computed(() => this.documents().find(d => d.key === CONTRACT_KEY) ?? this.documents()[0] ?? null);

  contractAccepted = false;
  waiver = false;
  usCitizen: '' | 'yes' | 'no' = '';
  citizenship = '';
  residence = '';
  paymentMethod: MpPaymentMethod | '' = '';
  cryptoToken: MpToken = 'USDT';
  countries: CountryOption[] = [];
  private formTick = signal(0);
  onInput(): void { this.formTick.update(n => n + 1); }

  usCitizenExcluded  = computed(() => { this.formTick(); return this.usCitizen === 'yes' || this.citizenship === 'US'; });
  usResidentExcluded = computed(() => { this.formTick(); return this.residence === 'US'; });

  canSubmit = computed(() => {
    this.formTick();
    return !this.submitting() && !!this.accepting() && !this.acceptBlock() && !!this.contractDoc()
      && this.contractAccepted && this.usCitizen === 'no' && !this.usCitizenExcluded()
      && !!this.citizenship && !!this.residence && !this.usResidentExcluded() && !!this.paymentMethod;
  });

  submitting  = signal(false);
  submitError = signal<string | null>(null);
  result      = signal<MpAcceptResponse | null>(null);

  // ----- Crypto hand-off (the membership wizard's web3 flow) ----------------------------
  cryptoOrderId   = signal<number | null>(null);
  cryptoAmount    = signal(0);
  cryptoRecipient = signal('');
  cryptoBusy      = signal(false);
  cryptoError     = signal<string | null>(null);

  // ----- My participations -----------------------------------------------------------------
  busyId    = signal<number | null>(null);
  rowError  = signal<{ id: number; message: string } | null>(null);
  pdfBusy   = signal<string | null>(null);

  constructor() {
    this.countries = countryOptions(this.lang());
    const wanted = this.route.snapshot.queryParamMap.get('tab') as Tab | null;
    this.load(wanted);
    this.loadDraftState();
    interval(60_000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.tick.set(Date.now()));
  }

  load(wantedTab: Tab | null = null): void {
    this.loading.set(true);
    this.service.overview()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: o => {
          const server = Date.parse(o.server_time);
          this.serverOffset.set(Number.isNaN(server) ? 0 : server - Date.now());
          this.tick.set(Date.now());
          this.overview.set(o);
          this.blocked.set(null);
          this.loading.set(false);
          // The new-participation form lives on the investments tab: an unlocked partner starts there.
          const tab = wantedTab ?? (o.investments.length || o.visible ? 'investments' : 'info');
          this.tab.set(tab === 'referrals' && !this.showReferrals() ? 'investments' : tab);
        },
        error: err => {
          this.loading.set(false);
          this.blocked.set(err?.status === 404 ? 'not_available' : 'error');
        }
      });
  }

  /** Only for the draft banner; a failure simply shows no banner. */
  private loadDraftState(): void {
    this.service.documents(this.docLanguage())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: res => this.docDraft.set((res.documents ?? []).some(d => d.draft)), error: () => undefined });
  }

  selectTab(tab: Tab): void {
    this.tab.set(tab);
  }

  // ----- New participation ------------------------------------------------------------------

  /**
   * Creates the partner's own draft and goes straight into the acceptance view
   * for it — the contract shown there carries the real numbers before anything
   * is ticked (customer correction 2026-10-28, point 4).
   */
  createDraft(): void {
    this.onNewInput();
    const cents = this.newCents();
    if (cents === null || !this.canCreate()) return;
    this.creating.set(true);
    this.createError.set(null);
    this.factorNotice.set(false);
    this.service.createInvestment((cents / 100).toFixed(2))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.creating.set(false);
          this.newAmount = '';
          this.onNewInput();
          this.refreshOverviewQuietly();
          this.startAccept(res.investment);
        },
        error: err => {
          this.creating.set(false);
          this.createError.set(this.errorText(err));
          const code = mpErrorCode(err);
          // The pool or the factor changed under the partner: show the current state.
          if (code === 'payout_factor_missing' || code === 'marketing_pool_not_activated'
              || code === 'contracts_not_released' || code === 'not_found') this.refreshOverviewQuietly();
        }
      });
  }

  // ----- Acceptance ------------------------------------------------------------------------

  startAccept(inv: MpInvestment): void {
    if (this.acceptBlock()) return;
    this.resetForm();
    this.submitError.set(null);
    this.accepting.set(inv);
    this.loadDocuments();
  }

  cancelAccept(): void {
    this.accepting.set(null);
    this.resetForm();
  }

  loadDocuments(): void {
    const inv = this.accepting();
    if (!inv) return;
    this.docsLoading.set(true);
    this.docsError.set(null);
    this.service.documents(this.docLanguage(), inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.documents.set(res.documents ?? []);
          this.contractAccepted = false;
          this.docsLoading.set(false);
          this.onInput();
        },
        error: err => {
          this.docsLoading.set(false);
          this.docsError.set(this.errorText(err));
        }
      });
  }

  submit(): void {
    this.onInput();
    const inv = this.accepting();
    const doc = this.contractDoc();
    if (!inv || !doc || !this.canSubmit()) return;
    const method = this.paymentMethod as MpPaymentMethod;

    this.submitting.set(true);
    this.submitError.set(null);
    this.service.accept(inv.id, {
      payment_method: method,
      ...(method === 'crypto' ? { crypto_token: this.cryptoToken } : {}),
      language: this.docLanguage(),
      declarations: { us_citizen: false, citizenship_country: this.citizenship, residence_country: this.residence },
      acceptances: [{ key: doc.key, version: doc.version }],
      waiver: this.waiver
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.submitting.set(false);
          this.accepting.set(null);
          this.resetForm();
          this.result.set(res);
          if (res.payment.method === 'crypto') this.startCrypto(res.payment.order_id, Number(res.payment.amount));
          this.refreshOverviewQuietly();
        },
        error: err => {
          this.submitting.set(false);
          this.submitError.set(this.errorText(err));
          const code = mpErrorCode(err);
          // The contract changed under the partner: show the new version, untick it.
          if (code === 'version_conflict' || code === 'acceptance_required') this.loadDocuments();
          if (code === 'not_offered') this.refreshOverviewQuietly();
          // Management changed the factor after the draft was made; the backend has
          // already withdrawn the draft — back to the form with the new factor.
          if (code === 'factor_changed') {
            this.submitError.set(null);
            this.cancelAccept();
            this.factorNotice.set(true);
            this.load('investments');
          }
        }
      });
  }

  /** Discarding a draft is the withdraw endpoint on an `offered` investment. */
  decline(inv: MpInvestment): void {
    if (this.busyId()) return;
    if (!window.confirm(this.translate.translate('marketing_pool.offer.decline_confirm', { reference: inv.reference }))) return;
    this.busyId.set(inv.id);
    this.rowError.set(null);
    this.service.withdraw(inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.busyId.set(null); this.load('investments'); },
        error: err => {
          this.busyId.set(null);
          this.rowError.set({ id: inv.id, message: this.errorText(err) });
        }
      });
  }

  /**
   * Crypto: the existing web3 flow of the membership wizard —
   * initiate_payment(order, 'web3_payment') for the recipient address, then
   * <app-web3-confirmation> (wallet, token, update_web3_wallet,
   * submit_web3_transaction). Also used for a pending crypto participation.
   */
  startCrypto(orderId: number, amount: number): void {
    this.cryptoBusy.set(true);
    this.cryptoError.set(null);
    this.cryptoRecipient.set('');
    this.cryptoOrderId.set(orderId);
    this.cryptoAmount.set(amount);
    this.orders.initiatePayment(orderId, 'web3_payment')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ip => {
          this.cryptoBusy.set(false);
          const recipient = ip?.payment_details?.recipient_address || '';
          this.cryptoRecipient.set(recipient);
          if (!recipient) this.cryptoError.set(this.translate.translate('marketing_pool.payment.crypto_init_failed'));
        },
        error: () => {
          this.cryptoBusy.set(false);
          this.cryptoError.set(this.translate.translate('marketing_pool.payment.crypto_init_failed'));
        }
      });
  }

  payPending(inv: MpInvestment): void {
    if (!inv.order_id) return;
    this.result.set(null);
    this.startCrypto(inv.order_id, Number(inv.amount_usd));
  }

  onCryptoSuccess(): void {
    this.cryptoOrderId.set(null);
    this.cryptoRecipient.set('');
    this.result.set(null);
    this.load('investments');
  }

  onCryptoBack(): void {
    this.cryptoOrderId.set(null);
    this.cryptoRecipient.set('');
  }

  finish(): void {
    this.result.set(null);
    this.cryptoOrderId.set(null);
    this.cryptoRecipient.set('');
    this.load('investments');
  }

  private resetForm(): void {
    this.usCitizen = '';
    this.citizenship = '';
    this.residence = '';
    this.paymentMethod = '';
    this.cryptoToken = 'USDT';
    this.contractAccepted = false;
    this.waiver = false;
    this.documents.set([]);
    this.docsError.set(null);
    this.onInput();
  }

  private refreshOverviewQuietly(): void {
    this.service.overview().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: o => this.overview.set(o),
      error: () => undefined
    });
  }

  withdraw(inv: MpInvestment): void {
    if (!inv.withdrawable || this.busyId()) return;
    if (!window.confirm(this.translate.translate('marketing_pool.investments.withdraw_confirm', { reference: inv.reference }))) return;
    this.busyId.set(inv.id);
    this.rowError.set(null);
    this.service.withdraw(inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.busyId.set(null); this.load('investments'); },
        error: err => {
          this.busyId.set(null);
          this.rowError.set({ id: inv.id, message: this.errorText(err) });
        }
      });
  }

  downloadPdf(inv: MpInvestment, type: string): void {
    const key = `${inv.id}:${type}`;
    if (this.pdfBusy()) return;
    this.pdfBusy.set(key);
    this.rowError.set(null);
    this.service.documentPdf(inv.id, type, this.docLanguage())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: blob => { this.pdfBusy.set(null); saveBlob(blob, `${inv.reference}-${type}.pdf`); },
        error: err => {
          this.pdfBusy.set(null);
          this.rowError.set({ id: inv.id, message: this.errorText(err) });
        }
      });
  }

  // ----- Display helpers -----------------------------------------------------------------

  /** Whole days until the payout date on the server clock; null without one. */
  daysLeft(dueAt: string | null): number | null {
    if (!dueAt) return null;
    const due = Date.parse(dueAt);
    if (Number.isNaN(due)) return null;
    const now = this.tick() + this.serverOffset();
    return Math.ceil((due - now) / DAY_MS);
  }

  /** A withdrawn row that was never accepted is a discarded (or replaced) draft, not a withdrawal. */
  statusKey(inv: MpInvestment): string {
    return inv.status === 'withdrawn' && !inv.accepted_at ? 'declined' : inv.status;
  }

  statusColor(status: MpStatus): string {
    return STATUS_COLOR[status] ?? 'secondary';
  }

  money(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return new Intl.NumberFormat(this.lang(), { style: 'currency', currency: 'USD' }).format(n);
  }

  /** The payout factor as "6.00×" in the page language; "—" without one. */
  factor(value: string | null | undefined): string {
    if (!value) return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return new Intl.NumberFormat(this.lang(), { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n) + '×';
  }

  date(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(this.lang(), { year: 'numeric', month: 'short', day: 'numeric' });
  }

  dateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(this.lang(), { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  country(code: string | null | undefined): string {
    return countryName(code, this.lang());
  }

  bankRows(bank: Record<string, string> | null | undefined): { key: string; value: string }[] {
    if (!bank) return [];
    return Object.entries(bank).filter(([, v]) => !!v).map(([key, value]) => ({ key, value }));
  }

  private errorText(err: unknown): string {
    return this.translate.translate(mpErrorKey(err), mpErrorParams(err));
  }

  /** The contract exists in de + en only. */
  docLanguage(): string {
    return this.lang().startsWith('de') ? 'de' : 'en';
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }

  goDashboard(): void {
    this.router.navigate(['/', this.lang(), 'dashboard']);
  }
}
